import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  Bell, 
  MessageSquare, 
  Users, 
  Send,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Loader,
  Folder,
  ExternalLink,
  BookOpen,
  Languages,
  UserPlus,
  Trash2,
  LogOut,
  Plus,
  CreditCard,
  Sparkles
} from 'lucide-react';
import { toSaudiTime, toSaudiDateTime } from '../utils/timeUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const { toast } = useToast();
  
  const [notifications, setNotifications] = useState([]);
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('notifications');
  
  // Group messaging states
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [newGroupMessage, setNewGroupMessage] = useState('');
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [projects, setProjects] = useState([]);

  // Fetch user's researcher profile
  const [researcherProfile, setResearcherProfile] = useState(null);
  
  // Subscription states
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // Team matching states
  const [teamMatches, setTeamMatches] = useState([]);
  const [teamRecommendations, setTeamRecommendations] = useState([]);
  const [teamMatchingLoading, setTeamMatchingLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    fetchResearcherProfile();
  }, [user]);

  // Handle PayPal return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paypalSuccess = urlParams.get('paypal_success');
    const paypalCancel = urlParams.get('paypal_cancel');
    const token = urlParams.get('token');
    
    if (paypalSuccess === 'true' && token && researcherProfile) {
      handlePayPalSuccess(token);
    } else if (paypalCancel === 'true') {
      toast({
        title: t('paymentCancelled') || "Payment Cancelled",
        description: t('paymentCancelledDesc') || "You cancelled the payment process.",
        variant: "default"
      });
      // Clean URL
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, [researcherProfile, location.search]);

  useEffect(() => {
    if (researcherProfile) {
      fetchNotifications();
      fetchConnectionRequests();
      fetchConnections();
      fetchUnreadCount();
      fetchProjects();
      checkDeadlines(); // Check for approaching deadlines on load
      
      // Refresh every 10 seconds
      const interval = setInterval(() => {
        fetchNotifications();
        fetchConnectionRequests();
        fetchUnreadCount();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [researcherProfile]);

  // Handle navigation from ResearcherProfiles page
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
      
      if (location.state.selectedResearcherId && connections.length > 0) {
        // Find the connection and select it
        const connection = connections.find(conn => 
          conn.from_researcher_id === location.state.selectedResearcherId || 
          conn.to_researcher_id === location.state.selectedResearcherId
        );
        
        if (connection) {
          selectConnection(connection);
        }
      }
    }
  }, [location.state, connections]);

  const fetchResearcherProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/`);
      if (response.ok) {
        const profiles = await response.json();
        const myProfile = profiles.find(p => p.email === user.email);
        if (myProfile) {
          setResearcherProfile(myProfile);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalSuccess = async (token) => {
    try {
      // Get order info from sessionStorage
      const orderInfoStr = sessionStorage.getItem('paypal_credit_order');
      if (!orderInfoStr) {
        toast({
          title: t('error') || "Error",
          description: "Payment information not found.",
          variant: "destructive"
        });
        window.history.replaceState({}, document.title, '/dashboard');
        return;
      }

      const orderInfo = JSON.parse(orderInfoStr);
      
      // Show loading toast
      toast({
        title: t('processingPayment') || "Processing Payment",
        description: t('pleaseWait') || "Please wait while we process your payment...",
      });

      // Capture the payment
      const response = await fetch(`${BACKEND_URL}/api/paypal/capture-credit-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderInfo.order_id,
          researcher_id: orderInfo.researcher_id
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Clear sessionStorage
        sessionStorage.removeItem('paypal_credit_order');
        
        // Show success message
        toast({
          title: t('purchaseSuccessful') || "Purchase Successful!",
          description: `${data.credits_added} ${t('creditsAdded') || 'credits added to your account'}`,
          variant: "default"
        });
        
        // Clean URL
        window.history.replaceState({}, document.title, '/dashboard');
        
        // Switch to subscription tab to see updated credits (optional)
        setActiveTab('subscription');
      } else {
        const errorData = await response.json();
        toast({
          title: t('paymentFailed') || "Payment Failed",
          description: errorData.detail || "Unable to complete purchase. Please contact support.",
          variant: "destructive"
        });
        window.history.replaceState({}, document.title, '/dashboard');
      }
    } catch (error) {
      console.error('Payment capture error:', error);
      toast({
        title: t('error') || "Error",
        description: "Failed to process payment. Please contact support.",
        variant: "destructive"
      });
      window.history.replaceState({}, document.title, '/dashboard');
    }
  };

  const fetchNotifications = async () => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/notifications/${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchConnectionRequests = async () => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/requests/${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setConnectionRequests(data);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const fetchConnections = async () => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/connected/${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setConnections(data);
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const fetchUnreadCount = async () => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/unread-count/${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchProjects = async () => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/?user_id=${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        // Show only first 5 active projects
        const activeProjects = data.filter(p => p.status === 'active').slice(0, 5);
        setProjects(activeProjects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchSubscription = async () => {
    if (!researcherProfile) return;
    setSubscriptionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/paypal/subscription-status/${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!researcherProfile || !window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    setCancellingSubscription(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/paypal/cancel-subscription?researcher_id=${researcherProfile.id}`, {
        method: 'POST'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Subscription cancelled successfully"
        });
        fetchSubscription();
        fetchResearcherProfile();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to cancel subscription",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive"
      });
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Group messaging functions
  const fetchGroups = async () => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/group-messages/groups/${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchGroupMessages = async (groupId) => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/group-messages/messages/${groupId}?researcher_id=${researcherProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setGroupMessages(data);
        // Mark messages as read
        data.forEach(msg => {
          if (!msg.read_by.includes(researcherProfile.id)) {
            markMessageAsRead(msg.id);
          }
        });
      }
    } catch (error) {
      console.error('Error fetching group messages:', error);
    }
  };

  const markMessageAsRead = async (messageId) => {
    try {
      await fetch(`${BACKEND_URL}/api/group-messages/messages/${messageId}/read?researcher_id=${researcherProfile.id}`, {
        method: 'PUT'
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || selectedGroupMembers.length === 0) {
      toast({
        title: t('error'),
        description: 'Please enter group name and select at least one member',
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/group-messages/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newGroupName,
          created_by: researcherProfile.id,
          members: selectedGroupMembers
        })
      });

      if (response.ok) {
        toast({
          title: t('groupCreated'),
          description: t('groupCreatedSuccess')
        });
        setNewGroupName('');
        setSelectedGroupMembers([]);
        setCreateGroupOpen(false);
        fetchGroups();
      }
    } catch (error) {
      console.error('Error creating group:', error);
      toast({
        title: t('error'),
        description: 'Failed to create group',
        variant: "destructive"
      });
    }
  };

  const handleSendGroupMessage = async () => {
    if (!newGroupMessage.trim() || !selectedGroup) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/group-messages/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          group_id: selectedGroup.id,
          from_researcher_id: researcherProfile.id,
          content: newGroupMessage
        })
      });

      if (response.ok) {
        setNewGroupMessage('');
        fetchGroupMessages(selectedGroup.id);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!confirm(t('confirmLeaveGroup'))) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/group-messages/groups/${groupId}/members/${researcherProfile.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('leftGroup'),
          description: 'You have left the group'
        });
        setSelectedGroup(null);
        fetchGroups();
      }
    } catch (error) {
      console.error('Error leaving group:', error);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm(t('confirmDeleteGroup'))) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/group-messages/groups/${groupId}?researcher_id=${researcherProfile.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('groupDeleted'),
          description: 'Group has been deleted'
        });
        setSelectedGroup(null);
        fetchGroups();
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      toast({
        title: t('error'),
        description: 'Failed to delete group. Only the creator can delete the group.',
        variant: "destructive"
      });
    }
  };

  const toggleGroupMember = (memberId) => {
    setSelectedGroupMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };


  const checkDeadlines = async () => {
    if (!researcherProfile) return;
    try {
      await fetch(`${BACKEND_URL}/api/projects/check-deadlines/${researcherProfile.id}`, {
        method: 'POST'
      });
      // Refresh notifications after checking deadlines
      fetchNotifications();
    } catch (error) {
      console.error('Error checking deadlines:', error);
    }
  };

  const fetchTeamMatching = async () => {
    if (!researcherProfile) return;
    setTeamMatchingLoading(true);
    try {
      // Fetch top 3 individual matches
      const matchesResponse = await fetch(
        `${BACKEND_URL}/api/team-matching/recommendations/${researcherProfile.id}?limit=3`
      );
      if (matchesResponse.ok) {
        const matchesData = await matchesResponse.json();
        setTeamMatches(matchesData.matches || []);
      }

      // Fetch top 2 team recommendations
      const teamsResponse = await fetch(
        `${BACKEND_URL}/api/team-matching/team-recommendations/${researcherProfile.id}?limit=2&language=${language}`
      );
      if (teamsResponse.ok) {
        const teamsData = await teamsResponse.json();
        setTeamRecommendations(teamsData.teams || []);
      }
    } catch (error) {
      console.error('Error fetching team matching:', error);
    } finally {
      setTeamMatchingLoading(false);
    }
  };

  // Helper function to translate team strengths and match reasons
  const translateTeamString = (str) => {
    if (!str) return str;
    
    // Team strengths translations
    const strengthsMap = {
      "Diverse academic perspectives": t('diverseAcademicPerspectives'),
      "Interdisciplinary expertise": t('interdisciplinaryExpertise'),
      "Focused domain knowledge": t('focusedDomainKnowledge'),
      "Strong research alignment": t('strongResearchAlignment'),
      "International collaboration": t('internationalCollaboration'),
      "Complementary skills": t('complementarySkills'),
    };
    
    // Match reasons translations
    const reasonsMap = {
      "Strong research interest alignment": t('strongResearchInterestAlignment'),
      "Good research interest match": t('goodResearchInterestMatch'),
      "Local collaboration opportunity": t('localCollaborationOpportunity'),
      "Complementary expertise levels": t('complementaryExpertiseLevels'),
      "Similar research profile": t('similarResearchProfile'),
    };
    
    // Check for exact match first
    if (strengthsMap[str]) return strengthsMap[str];
    if (reasonsMap[str]) return reasonsMap[str];
    
    // Check for patterns like "Same field: X" or "Prefers X collaboration"
    if (str.startsWith("Same field:")) {
      const field = str.replace("Same field:", "").trim();
      return `${t('sameField')}: ${field}`;
    }
    if (str.includes("collaboration") && str.startsWith("Prefers")) {
      const type = str.replace("Prefers", "").replace("collaboration", "").trim();
      return `${t('prefersCollaboration')} ${type}`;
    }
    
    return str;
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/request/${requestId}/accept`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        toast({
          title: t('requestAccepted'),
          description: t('connectionEstablished')
        });
        fetchConnectionRequests();
        fetchConnections();
        fetchNotifications();
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToSend'),
        variant: "destructive"
      });
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/request/${requestId}/reject`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        toast({
          title: t('requestDeclined'),
          description: t('noRequests')
        });
        fetchConnectionRequests();
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToSend'),
        variant: "destructive"
      });
    }
  };

  const fetchMessages = async (otherResearcherId) => {
    if (!researcherProfile) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/connections/messages/${researcherProfile.id}/${otherResearcherId}`
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConnection) {
      console.log('Message send prevented:', { newMessage, selectedConnection });
      return;
    }

    try {
      console.log('Sending message:', {
        from: researcherProfile.id,
        to: selectedConnection.id,
        content: newMessage
      });

      const response = await fetch(`${BACKEND_URL}/api/connections/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_researcher_id: researcherProfile.id,
          to_researcher_id: selectedConnection.id,
          content: newMessage
        })
      });

      console.log('Message response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Message sent successfully:', data);
        setNewMessage('');
        fetchMessages(selectedConnection.id);
        toast({
          title: t('messageSent'),
          description: t('messageDelivered')
        });
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to send message:', errorData);
        toast({
          title: t('error'),
          description: errorData.detail || t('failedToSend'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Message send error:', error);
      toast({
        title: t('error'),
        description: error.message || t('failedToSend'),
        variant: "destructive"
      });
    }
  };

  const selectConnection = (connection) => {
    console.log('Selecting connection:', connection);
    console.log('Current researcher profile:', researcherProfile);
    
    const otherResearcher = connection.from_researcher_id === researcherProfile.id
      ? { id: connection.to_researcher_id, name: connection.to_researcher_name }
      : { id: connection.from_researcher_id, name: connection.from_researcher_name };
    
    console.log('Other researcher:', otherResearcher);
    setSelectedConnection(otherResearcher);
    fetchMessages(otherResearcher.id);
    
    // Automatically switch to messages tab
    setActiveTab('messages');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (!researcherProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>{t('noProfileFound')}</CardTitle>
            <CardDescription>{t('createProfileFirst')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              {t('goToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('home')}
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">{t('researcherDashboard')}</h1>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="flex items-center gap-2"
              >
                <Languages className="h-4 w-4" />
                {language === 'en' ? 'العربية' : 'English'}
              </Button>
              <div className="text-right">
                <p className="text-sm font-medium">{researcherProfile.name}</p>
                <p className="text-xs text-gray-500">{researcherProfile.university}</p>
              </div>
              <Button variant="outline" onClick={logout}>
                {t('logout')}
              </Button>
            </div>
          </div>
          
          {/* Quick Access Navigation */}
          <div className="flex items-center justify-between gap-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveTab('notifications')}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'notifications' 
                    ? 'bg-cyan-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Bell className="h-4 w-4" />
                {t('notifications')}
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setActiveTab('connections')}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'connections' 
                    ? 'bg-cyan-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Users className="h-4 w-4" />
                {t('myConnections')}
              </button>
              
              <button
                onClick={() => setActiveTab('messages')}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'messages' 
                    ? 'bg-cyan-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <MessageSquare className="h-4 w-4" />
                {t('messages')}
              </button>
              
              <button
                onClick={() => {
                  setActiveTab('groups');
                  fetchGroups();
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'groups' 
                    ? 'bg-cyan-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Users className="h-4 w-4" />
                {t('groups')}
              </button>
              
              <button
                onClick={() => {
                  setActiveTab('subscription');
                  fetchSubscription();
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'subscription' 
                    ? 'bg-cyan-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <CreditCard className="h-4 w-4" />
                {t('subscription') || 'Subscription'}
              </button>
              
              <button
                onClick={() => {
                  setActiveTab('team-matching');
                  fetchTeamMatching();
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'team-matching' 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Users className="h-4 w-4" />
                {t('findTeam') || 'Find Team'}
              </button>
              
              <button
                onClick={() => navigate('/projects')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                <Folder className="h-4 w-4" />
                {t('projects')}
              </button>
              
              <button
                onClick={() => navigate('/library')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                <BookOpen className="h-4 w-4" />
                {t('library')}
              </button>
            </div>
          </div>
          
          {/* Active Projects - Clickable */}
          {projects.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-gray-100 mt-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Folder className="h-4 w-4 text-cyan-600" />
                <span className="font-medium">{t('myProjects')}:</span>
              </div>
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-full text-sm transition-colors"
                >
                  <span>{project.title}</span>
                  <ExternalLink className="h-3 w-3" />
                </button>
              ))}
              <button
                onClick={() => navigate('/projects')}
                className="inline-flex items-center gap-1 px-3 py-1 text-gray-500 hover:text-cyan-600 text-sm transition-colors"
              >
                {t('viewAllProjects')} →
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('notifications')}</CardTitle>
                <CardDescription>{t('stayTuned')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {notifications.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('noNotifications')}</p>
                    <p className="text-sm text-gray-400 mt-2">{t('stayTuned')}</p>
                  </div>
                ) : (
                  notifications.map((notif) => {
                    // Translate notification titles and messages
                    const translateNotifTitle = (title) => {
                      const titleMap = {
                        "New Message": t('newMessage') || "New Message",
                        "Connection Accepted": t('connectionAccepted') || "Connection Accepted",
                        "Connection Request": t('connectionRequest') || "Connection Request",
                        "New Announcement": t('newAnnouncement') || "New Announcement",
                      };
                      return titleMap[title] || title;
                    };
                    
                    const translateNotifMessage = (message) => {
                      if (!message) return message;
                      
                      // Translate "accepted your connection request" pattern
                      if (message.includes("accepted your connection request")) {
                        const name = message.replace(" accepted your connection request", "");
                        return `${name} ${t('acceptedYourConnectionRequest') || 'accepted your connection request'}`;
                      }
                      
                      // Translate "sent you a connection request" pattern
                      if (message.includes("sent you a connection request")) {
                        const name = message.replace(" sent you a connection request", "");
                        return `${name} ${t('sentYouConnectionRequest') || 'sent you a connection request'}`;
                      }
                      
                      return message;
                    };
                    
                    return (
                      <div
                        key={notif.id}
                        className={`p-4 border rounded-lg ${notif.read ? 'bg-white' : 'bg-cyan-50 border-cyan-200'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold">{translateNotifTitle(notif.title)}</h4>
                            <p className="text-sm text-gray-600">{translateNotifMessage(notif.message)}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {toSaudiDateTime(notif.created_at)}
                            </p>
                          </div>
                          {!notif.read && (
                            <Badge className="bg-cyan-500">{t('new') || 'New'}</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* Connection Requests */}
            {connectionRequests.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('connectionRequests')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {connectionRequests.map((request) => (
                    <div key={request.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{request.from_researcher_name}</h4>
                          <p className="text-sm text-gray-600">{request.from_researcher_email}</p>
                          {request.message && (
                            <p className="text-sm text-gray-500 mt-2 italic">&quot;{request.message}&quot;</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-cyan-500 hover:bg-cyan-600"
                            onClick={() => handleAcceptRequest(request.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {t('acceptRequest')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectRequest(request.id)}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            {t('declineRequest')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Connections Tab */}
          <TabsContent value="connections">
            <Card>
              <CardHeader>
                <CardTitle>{t('myConnections')}</CardTitle>
                <CardDescription>{t('startConnecting')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {connections.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">{t('noConnections')}</p>
                    <p className="text-sm text-gray-400 mb-6">{t('startConnecting')}</p>
                    <Button onClick={() => navigate('/profiles')} className="bg-cyan-500 hover:bg-cyan-600">
                      <Users className="h-4 w-4 mr-2" />
                      {t('browseResearchers')}
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {connections.map((connection) => {
                      const isFrom = connection.from_researcher_id === researcherProfile.id;
                      const otherName = isFrom ? connection.to_researcher_name : connection.from_researcher_name;
                      const otherEmail = isFrom ? connection.to_researcher_email : connection.from_researcher_email;
                      
                      return (
                        <div key={connection.id} className="p-4 border rounded-lg">
                          <h4 className="font-semibold">{otherName}</h4>
                          <p className="text-sm text-gray-600">{otherEmail}</p>
                          <Button
                            size="sm"
                            className="mt-2 bg-cyan-500 hover:bg-cyan-600"
                            onClick={() => selectConnection(connection)}
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Message
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>{t('messages')}</CardTitle>
                <CardDescription>{t('selectConnection')}</CardDescription>
              </CardHeader>
              <CardContent>
                {!selectedConnection ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('noMessages')}</p>
                    <p className="text-sm text-gray-400 mt-2">{t('selectConnection')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="border-b pb-4">
                      <h3 className="font-semibold">{selectedConnection.name}</h3>
                    </div>
                    
                    <div className="h-96 overflow-y-auto space-y-4 p-4 bg-gray-50 rounded-lg">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.from_researcher_id === researcherProfile.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs p-3 rounded-lg ${
                              msg.from_researcher_id === researcherProfile.id
                                ? 'bg-cyan-500 text-white'
                                : 'bg-white border'
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.from_researcher_id === researcherProfile.id ? 'text-cyan-100' : 'text-gray-400'
                            }`}>
                              {toSaudiTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        placeholder={t('typeMessage')}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        required
                      />
                      <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600">
                        <Send className="h-4 w-4" />
                      </Button>
                    </form>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Groups Tab */}
          <TabsContent value="groups">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('groupMessages')}</CardTitle>
                  <Button onClick={() => setCreateGroupOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    {t('createGroup')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!selectedGroup ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groups.length > 0 ? (
                      groups.map((group) => (
                        <Card
                          key={group.id}
                          className="cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => {
                            setSelectedGroup(group);
                            fetchGroupMessages(group.id);
                          }}
                        >
                          <CardContent className="pt-6">
                            <div className="flex items-start justify-between mb-3">
                              <h3 className="font-semibold text-lg">{group.name}</h3>
                              <Users className="h-5 w-5 text-gray-400" />
                            </div>
                            <div className="text-sm text-gray-600">
                              {group.members.length} {group.members.length === 1 ? t('member') : t('members')}
                            </div>
                            {group.created_by === researcherProfile?.id && (
                              <span className="inline-block mt-2 px-2 py-1 bg-cyan-100 text-cyan-700 text-xs rounded">
                                {t('creator')}
                              </span>
                            )}
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-12">
                        <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('noGroups')}</h3>
                        <p className="text-gray-600 mb-4">{t('noGroupsDesc')}</p>
                        <Button onClick={() => setCreateGroupOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          {t('createGroup')}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4 pb-4 border-b">
                      <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedGroup(null)}>
                          <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div>
                          <h3 className="font-semibold text-lg">{selectedGroup.name}</h3>
                          <p className="text-sm text-gray-600">{selectedGroup.members.length} {t('members')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedGroup.created_by === researcherProfile?.id ? (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteGroup(selectedGroup.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLeaveGroup(selectedGroup.id)}
                          >
                            <LogOut className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                      {groupMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.from_researcher_id === researcherProfile?.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs px-4 py-2 rounded-lg ${
                              message.from_researcher_id === researcherProfile?.id
                                ? 'bg-cyan-500 text-white'
                                : 'bg-gray-200 text-gray-900'
                            }`}
                          >
                            {message.from_researcher_id !== researcherProfile?.id && (
                              <div className="text-xs font-semibold mb-1">{message.from_researcher_name}</div>
                            )}
                            <p>{message.content}</p>
                            <div className={`text-xs mt-1 ${message.from_researcher_id === researcherProfile?.id ? 'text-cyan-100' : 'text-gray-500'}`}>
                              {new Date(message.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSendGroupMessage();
                      }}
                      className="flex gap-2"
                    >
                      <Input
                        value={newGroupMessage}
                        onChange={(e) => setNewGroupMessage(e.target.value)}
                        placeholder={t('typeMessage')}
                        className="flex-1"
                      />
                      <Button type="submit">
                        <Send className="h-4 w-4" />
                      </Button>
                    </form>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-cyan-600" />
                  {t('subscription') || 'Subscription'}
                </CardTitle>
                <CardDescription>
                  {t('manageSubscription') || 'Manage your subscription and view your plan details'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {subscriptionLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="h-8 w-8 animate-spin text-cyan-600" />
                  </div>
                ) : researcherProfile ? (
                  <div className="space-y-6">
                    {/* Current Plan */}
                    <div className="border rounded-lg p-6 bg-gradient-to-br from-cyan-50 to-white">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 capitalize">
                            {researcherProfile.subscription_plan === 'basic' && t('basicPlan')}
                            {researcherProfile.subscription_plan === 'premium' && t('premiumPlan')}
                            {researcherProfile.subscription_plan === 'advanced' && t('advancedPlan')}
                            {!researcherProfile.subscription_plan && t('basicPlan')}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {researcherProfile.subscription_status === 'active' && t('activeSubscription')}
                            {researcherProfile.subscription_status === 'cancelled' && t('cancelled')}
                            {researcherProfile.subscription_status === 'pending' && t('pendingPayment')}
                            {!researcherProfile.subscription_status && t('freePlan')}
                          </p>
                        </div>
                        {researcherProfile.subscription_plan !== 'basic' && (
                          <Badge className={`
                            ${researcherProfile.subscription_plan === 'advanced' ? 'bg-purple-500' : 'bg-cyan-500'}
                          `}>
                            {researcherProfile.subscription_plan === 'advanced' ? 'Premium' : 'Pro'}
                          </Badge>
                        )}
                      </div>

                      {/* Plan Features */}
                      <div className="space-y-3 mb-6">
                        {/* Basic Plan Features */}
                        {researcherProfile.subscription_plan === 'basic' && (
                          <>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">50 {t('aiCredits') || 'AI Credits'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">3 {t('researchProjects') || 'Research Projects'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">5 GB {t('storage') || 'Storage'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('basicVirtualRooms') || 'Basic Virtual Rooms'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('communitySupport') || 'Community Support'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('basicDocManagement') || 'Basic Document Management'}</span>
                            </div>
                          </>
                        )}

                        {/* Premium Plan Features */}
                        {researcherProfile.subscription_plan === 'premium' && (
                          <>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm font-semibold">500 {t('aiCredits') || 'AI Credits'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('tenResearchProjects') || '10 Research Projects'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">50 GB {t('storage') || 'Storage'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('extendedVirtualRooms') || 'Extended Virtual Rooms'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('prioritySupport') || 'Priority Support'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('advancedAI') || 'Advanced AI Features'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('realtimeCollaboration') || 'Real-time Collaboration'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('exportFormats') || 'Multiple Export Formats'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('documentSharingVersioning') || 'Document Sharing & Versioning'}</span>
                            </div>
                          </>
                        )}

                        {/* Advanced Plan Features */}
                        {researcherProfile.subscription_plan === 'advanced' && (
                          <>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm font-semibold">2,500 {t('aiCredits') || 'AI Credits'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('unlimitedProjectsCollaborators') || 'Unlimited Projects & Collaborators'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">500 GB {t('storage') || 'Storage'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('unlimitedRoomsRecording') || 'Unlimited Rooms & Recording'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('support247') || '24/7 Priority Support'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('advancedAnalyticsDashboard') || 'Advanced Analytics Dashboard'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('apiAccessIntegrations') || 'API Access & Integrations'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('customDataExport') || 'Custom Data Export'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('enhancedSecurity') || 'Enhanced Security'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm">{t('dedicatedSupportChannel') || 'Dedicated Support Channel'}</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* AI Credits Usage */}
                      {researcherProfile.credits_used !== undefined && (
                        <div className="bg-white rounded-lg p-4 border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">AI Credits Usage</span>
                            <span className="text-sm font-bold text-cyan-600">
                              {researcherProfile.credits_used} / {researcherProfile.subscription_plan === 'advanced' ? '2500' : researcherProfile.subscription_plan === 'premium' ? '500' : '50'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-cyan-600 h-2 rounded-full transition-all"
                              style={{ 
                                width: `${Math.min(100, (researcherProfile.credits_used / (researcherProfile.subscription_plan === 'advanced' ? 2500 : researcherProfile.subscription_plan === 'premium' ? 500 : 50)) * 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Subscription Details */}
                      {subscription && subscription.subscription && (
                        <div className="mt-6 pt-6 border-t space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Status:</span>
                            <span className="font-medium capitalize">{subscription.subscription.status}</span>
                          </div>
                          {subscription.subscription.next_billing_date && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Next Billing:</span>
                              <span className="font-medium">
                                {new Date(subscription.subscription.next_billing_date).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                      {researcherProfile.subscription_plan === 'basic' ? (
                        <Button
                          onClick={() => navigate('/subscription')}
                          className="bg-cyan-500 hover:bg-cyan-600"
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          {t('upgradePlan')}
                        </Button>
                      ) : (
                        <>
                          <Button
                            onClick={() => navigate('/subscription')}
                            variant="outline"
                          >
                            {t('changePlan')}
                          </Button>
                          {researcherProfile.subscription_status !== 'cancelled' && (
                            <Button
                              onClick={handleCancelSubscription}
                              disabled={cancellingSubscription}
                              variant="destructive"
                            >
                              {cancellingSubscription ? (
                                <>
                                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                                  {t('cancelling')}
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 mr-2" />
                                  {t('cancelSubscription')}
                                </>
                              )}
                            </Button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Subscription History */}
                    {subscription && subscription.transactions && subscription.transactions.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-4">Transaction History</h4>
                        <div className="space-y-2">
                          {subscription.transactions.slice(0, 5).map((transaction, idx) => (
                            <div key={idx} className="flex justify-between text-sm py-2 border-b last:border-0">
                              <span className="text-gray-600">
                                {new Date(transaction.created_at).toLocaleDateString()}
                              </span>
                              <span className="font-medium">${transaction.amount}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Loading profile...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Matching Tab */}
          <TabsContent value="team-matching">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  {t('findYourResearchTeam') || 'Find Your Research Team'}
                </CardTitle>
                <CardDescription>
                  {t('teamMatchingDescription') || 'AI-powered matching to discover researchers and teams with similar interests'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {teamMatchingLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="h-8 w-8 animate-spin text-purple-600" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="h-4 w-4 text-purple-600" />
                          <span className="text-sm text-gray-600">{t('yourMatches') || 'Your Matches'}</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-600">{teamMatches.length}</p>
                      </div>
                      <div className="p-4 bg-cyan-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="h-4 w-4 text-cyan-600" />
                          <span className="text-sm text-gray-600">{t('teamSuggestions') || 'Team Suggestions'}</span>
                        </div>
                        <p className="text-2xl font-bold text-cyan-600">{teamRecommendations.length}</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-cyan-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="h-4 w-4 text-purple-600" />
                          <span className="text-sm text-gray-600">{t('status') || 'Status'}</span>
                        </div>
                        <p className="text-sm font-semibold text-purple-600">{t('active') || 'Active'}</p>
                      </div>
                    </div>

                    {/* Top Team Recommendations */}
                    {teamRecommendations.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">{t('recommendedTeams') || 'Recommended Teams'}</h3>
                        <div className="space-y-4">
                          {teamRecommendations.map((team) => (
                            <div key={team.team_id} className="border border-purple-200 rounded-lg p-4 bg-gradient-to-r from-purple-50/50 to-white">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold">{t('team') || 'Team'} {team.team_id.split('_')[1]}</h4>
                                  <p className="text-sm text-gray-600">{team.members.length} {t('members') || 'members'}</p>
                                </div>
                                <Badge className="bg-purple-100 text-purple-700 border-0">
                                  {team.synergy_score}% {t('match') || 'Match'}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-3 gap-2 mb-3">
                                {team.members.map((member, idx) => (
                                  <div key={idx} className="text-xs">
                                    <div className="flex items-center gap-1">
                                      <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-semibold text-cyan-700">
                                        {member.name.charAt(0)}
                                      </div>
                                      <span className="truncate font-medium">{member.name}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {(team.suggested_research_topics || team.suggested_research_titles) && (team.suggested_research_topics || team.suggested_research_titles).length > 0 && (
                                <div className="mb-2">
                                  <p className="text-xs font-medium text-gray-700 mb-1">{t('suggestedResearchTopics') || 'Suggested Topic'}:</p>
                                  <p className="text-xs text-gray-600 italic">&quot;{(team.suggested_research_topics || team.suggested_research_titles)[0]}&quot;</p>
                                </div>
                              )}

                              {team.team_strengths && team.team_strengths.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {team.team_strengths.slice(0, 2).map((strength, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {translateTeamString(strength)}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Individual Matches */}
                    {teamMatches.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">{t('topResearcherMatches') || 'Top Researcher Matches'}</h3>
                        <div className="space-y-3">
                          {teamMatches.map((match) => (
                            <div key={match.researcher_id} className="border rounded-lg p-3 hover:border-cyan-300 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium">{match.name}</h4>
                                  <p className="text-sm text-gray-600">{match.university}</p>
                                  {match.research_field && (
                                    <p className="text-xs text-gray-500 mt-1">{match.research_field}</p>
                                  )}
                                </div>
                                <Badge className="bg-cyan-100 text-cyan-700 border-0">
                                  {Math.round(match.match_score * 100)}%
                                </Badge>
                              </div>
                              {match.match_reasons && match.match_reasons.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {match.match_reasons.slice(0, 2).map((reason, idx) => (
                                    <span key={idx} className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                      {translateTeamString(reason)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* View All Button */}
                    <div className="pt-4 border-t">
                      <Button 
                        onClick={() => navigate('/team-matching')}
                        className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600"
                      >
                        {t('viewAllMatchesAndTeams') || 'View All Matches & Teams'}
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </Button>
                    </div>

                    {/* Empty State */}
                    {teamMatches.length === 0 && teamRecommendations.length === 0 && (
                      <div className="text-center py-12">
                        <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">{t('noMatchesFoundYet') || 'No matches found yet'}</p>
                        <Button 
                          onClick={() => {
                            if (researcherProfile) {
                              fetchTeamMatching();
                            }
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {t('findMatches') || 'Find Matches'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Create Group Dialog */}
          <Dialog open={createGroupOpen} onOpenChange={setCreateGroupOpen}>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('createGroup')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('groupName')}</label>
                  <Input
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder={t('enterGroupName')}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('selectMembers')}</label>
                  <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-2">
                    {connections.filter(c => c.status === 'accepted').map((connection) => {
                      const other = connection.from_researcher_id === researcherProfile?.id
                        ? connection.to_researcher_id
                        : connection.from_researcher_id;
                      const otherName = connection.from_researcher_id === researcherProfile?.id
                        ? connection.to_researcher_name
                        : connection.from_researcher_name;
                      
                      return (
                        <div
                          key={connection.id}
                          className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          onClick={() => toggleGroupMember(other)}
                        >
                          <Checkbox
                            checked={selectedGroupMembers.includes(other)}
                            onCheckedChange={() => toggleGroupMember(other)}
                          />
                          <span>{otherName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateGroupOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button onClick={handleCreateGroup}>
                  {t('createGroup')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;
